import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSectionScrollProgress } from "@/hooks/useSharedScroll";

const VIDEO_URL =
  "https://public.readdy.ai/ai/video_res/019f21a3-a24f-7593-9ec0-056d13acb009.mp4";

// Frame count for the video scrub — lower = lighter on GPU memory + faster
// first-paint. 72 frames over a 500vh runway means one frame per ~6.9vh of
// scroll, which is still visually smooth (the eye can't resolve much finer
// than that in a scrub). 120 was overkill and cost a lot of GPU memory.
const MAX_FRAMES = 72;

// Progressive-highlight quote, revealed word-by-word as user scrolls
const REVEAL_QUOTE =
  "Healing isn't about fixing what's broken. It's about understanding what's been carrying weight for too long. You don't need to have the right words. You just need a space where every version of you is welcome.";
const REVEAL_WORDS = REVEAL_QUOTE.split(" ");

export default function Hero() {
  const sectionRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const posterVideoRef = useRef<HTMLVideoElement>(null);
  const framesRef = useRef<ImageBitmap[]>([]);
  const framesReadyCountRef = useRef(0);

  // Scroll progress lives only in refs — never in React state — so scrolling
  // never triggers a re-render. Direct 1:1 mapping from scroll to visuals.
  const progressRef = useRef(0);
  const rafPendingRef = useRef(false);
  const lastPaintedFrameRef = useRef(-1);
  const lastPaintedZoomRef = useRef(-1);
  const lastPaintedRef = useRef({
    leftOpacity: -1,
    leftY: -999,
    rightOpacity: -1,
    wordmarkY: -999,
    quoteOpacity: -1,
  });
  const lastWordAlphaRef = useRef<number[]>([]);

  // DOM refs for the scroll-driven elements
  const leftBlockRef = useRef<HTMLDivElement>(null);
  const wordmarkRef = useRef<HTMLDivElement>(null);
  const scrollHintRef = useRef<HTMLDivElement>(null);
  const quoteWrapperRef = useRef<HTMLDivElement>(null);
  const wordSpansRef = useRef<Array<HTMLSpanElement | null>>([]);

  // Track whether we have at least one frame ready — used to fade poster out.
  const [hasFirstFrame, setHasFirstFrame] = useState(false);

  // Phase 1: Download video as blob, then extract frames into an ImageBitmap
  // array. Frames are pushed into framesRef as they become available, so
  // scroll interaction works immediately without waiting for the full set.
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    // Hold onto the reader so cleanup can cancel it if the user navigates
    // away mid-download. Without this the fetch keeps buffering to memory
    // even after unmount — a slow leak, not a fast one, but still a leak.
    let activeReader: ReadableStreamDefaultReader<Uint8Array> | null = null;
    const collectedFrames: ImageBitmap[] = [];

    const run = async () => {
      try {
        const res = await fetch(VIDEO_URL);
        if (!res.ok || !res.body) throw new Error("fetch failed");

        const reader = res.body.getReader();
        activeReader = reader;
        const chunks: Uint8Array[] = [];

        while (true) {
          if (cancelled) {
            try {
              reader.cancel();
            } catch {
              /* ignore */
            }
            return;
          }
          const { done, value } = await reader.read();
          if (done) break;
          if (value) chunks.push(value);
        }
        activeReader = null;
        if (cancelled) return;

        const blob = new Blob(chunks, { type: "video/mp4" });
        objectUrl = URL.createObjectURL(blob);

        // Feed the poster video with the same blob so it can act as a
        // first-frame fallback until bitmaps are ready.
        const posterVideo = posterVideoRef.current;
        if (posterVideo) {
          posterVideo.src = objectUrl;
          try {
            posterVideo.load();
          } catch {
            /* ignore */
          }
        }

        const video = document.createElement("video");
        video.src = objectUrl;
        video.muted = true;
        video.playsInline = true;
        video.preload = "auto";
        video.crossOrigin = "anonymous";

        await new Promise<void>((resolve, reject) => {
          const onLoaded = () => resolve();
          const onError = () => reject(new Error("video load error"));
          video.addEventListener("loadedmetadata", onLoaded, { once: true });
          video.addEventListener("error", onError, { once: true });
        });
        if (cancelled) return;

        // Bitmap resolution: cap at 960 on the long edge. Smaller source
        // texture = faster sampling in drawImage. Still crisp enough at
        // 1080p thanks to source-rect zoom cropping.
        const maxDim = 960;
        const nativeW = video.videoWidth;
        const nativeH = video.videoHeight;
        const scaleDown = Math.min(1, maxDim / Math.max(nativeW, nativeH));
        const bitmapW = Math.round(nativeW * scaleDown);
        const bitmapH = Math.round(nativeH * scaleDown);

        // Canvas is sized to the actual display area (DPR capped at 1.0).
        const canvas = canvasRef.current;
        if (canvas) {
          const dpr = Math.min(1.0, window.devicePixelRatio || 1);
          const w = Math.round(window.innerWidth * dpr);
          const h = Math.round(window.innerHeight * dpr);
          canvas.width = w;
          canvas.height = h;
        }

        const duration = video.duration;
        if (!duration || !Number.isFinite(duration)) {
          throw new Error("invalid duration");
        }
        const frameCount = MAX_FRAMES;

        for (let i = 0; i < frameCount; i++) {
          if (cancelled) return;
          const t = (i / (frameCount - 1)) * Math.max(0, duration - 0.03);

          await new Promise<void>((resolve) => {
            const onSeeked = () => {
              video.removeEventListener("seeked", onSeeked);
              resolve();
            };
            video.addEventListener("seeked", onSeeked);
            try {
              video.currentTime = t;
            } catch {
              video.removeEventListener("seeked", onSeeked);
              resolve();
            }
          });

          try {
            const bitmap = await createImageBitmap(video, {
              resizeWidth: bitmapW,
              resizeHeight: bitmapH,
              resizeQuality: "high",
            });
            collectedFrames.push(bitmap);
            framesRef.current = collectedFrames;
            framesReadyCountRef.current = collectedFrames.length;

            // As soon as we have the first frame, kick a paint and drop the
            // poster fallback so the canvas takes over.
            if (collectedFrames.length === 1 && !cancelled) {
              setHasFirstFrame(true);
              lastPaintedFrameRef.current = -1;
              lastPaintedZoomRef.current = -1;
              schedulePaint();
            }
          } catch {
            /* skip */
          }
        }
      } catch {
        /* silently degrade — poster video continues to show first frame */
      }
    };

    void run();

    return () => {
      cancelled = true;
      if (activeReader) {
        try {
          activeReader.cancel();
        } catch {
          /* ignore */
        }
        activeReader = null;
      }
      framesRef.current.forEach((f) => {
        try {
          f.close?.();
        } catch {
          /* ignore */
        }
      });
      framesRef.current = [];
      collectedFrames.forEach((f) => {
        try {
          f.close?.();
        } catch {
          /* ignore */
        }
      });
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, []);

  // Paint a specific frame with a specific zoom level.
  // Zoom is done via SOURCE cropping in drawImage — NO CSS transform on the
  // canvas — which means zero compositing cost from scaling a big surface.
  const paintFrame = (idx: number, zoom: number) => {
    const canvas = canvasRef.current;
    const frames = framesRef.current;
    if (!canvas || !frames.length) return;
    // Clamp to what's currently available (frames may still be loading).
    const clamped = Math.max(0, Math.min(frames.length - 1, idx));
    const frame = frames[clamped];
    if (!frame) return;
    // desynchronized hint lets the browser paint outside the normal composite
    // path when possible — this is where a lot of the "silky" feel comes from.
    const ctx = canvas.getContext("2d", {
      alpha: false,
      desynchronized: true,
    });
    if (!ctx) return;

    const bw = frame.width;
    const bh = frame.height;
    // Source rect: shrinks (crops) as zoom grows, giving a smooth zoom-in.
    const srcW = bw / zoom;
    const srcH = bh / zoom;
    const srcX = (bw - srcW) / 2;
    const srcY = (bh - srcH) / 2;
    ctx.drawImage(
      frame,
      srcX,
      srcY,
      srcW,
      srcH,
      0,
      0,
      canvas.width,
      canvas.height
    );
  };

  // Keep canvas sized to viewport on resize
  useEffect(() => {
    const resize = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const dpr = Math.min(1.0, window.devicePixelRatio || 1);
      canvas.width = Math.round(window.innerWidth * dpr);
      canvas.height = Math.round(window.innerHeight * dpr);
      // Force a repaint after resize
      lastPaintedFrameRef.current = -1;
      lastPaintedZoomRef.current = -1;
      schedulePaint();
    };
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  // Once the first bitmap frame is ready, fully tear down the poster video:
  // pause, drop its src, and reload so it stops decoding in the background.
  // Just setting opacity to 0 was still costing us GPU/composite work.
  useEffect(() => {
    if (!hasFirstFrame) return;
    // Give the fade-out one tick to finish before we kill the source.
    const t = window.setTimeout(() => {
      const posterVideo = posterVideoRef.current;
      if (!posterVideo) return;
      try {
        posterVideo.pause();
      } catch {
        /* ignore */
      }
      try {
        posterVideo.removeAttribute("src");
        posterVideo.load();
      } catch {
        /* ignore */
      }
    }, 450);
    return () => window.clearTimeout(t);
  }, [hasFirstFrame]);

  // Single-flight rAF throttle: one paint per animation frame, direct 1:1
  // mapping from scroll position to visuals (no smoothing, no continuous
  // rAF loop). This gives perfect scroll sync and stops immediately when
  // the user stops scrolling — nothing keeps running in the background.
  const schedulePaint = () => {
    if (rafPendingRef.current) return;
    rafPendingRef.current = true;
    requestAnimationFrame(paint);
  };

  // In-view flag — updated by the shared scroll broadcaster (which already
  // knows every section's cached geometry). We NEVER call getBoundingClientRect
  // on the scroll path here — that was a hidden forced synchronous layout
  // costing multiple ms per frame.
  const inViewRef = useRef(true);

  const paint = () => {
    rafPendingRef.current = false;

    // Viewport early-exit: if Hero is off-screen, do zero work. Uses the
    // cached `inView` flag from useSectionScrollProgress — no layout query.
    if (!inViewRef.current) return;

    const progress = progressRef.current;

    // Compute scroll-linked values
    const leftFade = Math.min(1, progress / 0.15);
    const leftOpacity = 1 - leftFade;
    const leftTranslateY = -leftFade * 20;

    const rightFade = Math.min(1, progress / 0.3);
    const rightOpacity = 1 - rightFade;
    const wordmarkTranslateY = Math.min(1, progress / 0.3) * 45;

    const zoomStart = 0.3;
    const zoomProgress = Math.max(
      0,
      Math.min(1, (progress - zoomStart) / (1 - zoomStart))
    );
    const videoZoom = 1 + zoomProgress * 0.42;

    const quoteOpacity = Math.max(0, Math.min(1, (progress - 0.3) / 0.08));

    const revealStart = 0.38;
    const revealEnd = 0.92;
    const revealProgress = Math.max(
      0,
      Math.min(1, (progress - revealStart) / (revealEnd - revealStart))
    );

    // 1) Paint canvas — only if frame OR zoom changed enough to matter.
    const readyCount = framesReadyCountRef.current;
    if (readyCount > 0) {
      const idx = Math.round(progress * (MAX_FRAMES - 1));
      const zoomChanged =
        Math.abs(videoZoom - lastPaintedZoomRef.current) > 0.003;
      const frameChanged = idx !== lastPaintedFrameRef.current;
      if (frameChanged || zoomChanged) {
        paintFrame(idx, videoZoom);
        lastPaintedFrameRef.current = idx;
        lastPaintedZoomRef.current = videoZoom;
      }
    }

    // 2) Push text/DOM updates only when values actually shift
    const last = lastPaintedRef.current;

    const leftEl = leftBlockRef.current;
    if (leftEl) {
      if (Math.abs(leftOpacity - last.leftOpacity) > 0.005) {
        leftEl.style.opacity = leftOpacity.toFixed(3);
        last.leftOpacity = leftOpacity;
      }
      if (Math.abs(leftTranslateY - last.leftY) > 0.05) {
        leftEl.style.transform = `translate3d(0, ${leftTranslateY.toFixed(
          2
        )}vh, 0)`;
        last.leftY = leftTranslateY;
      }
    }

    const wordmarkEl = wordmarkRef.current;
    if (wordmarkEl) {
      if (Math.abs(rightOpacity - last.rightOpacity) > 0.005) {
        wordmarkEl.style.opacity = rightOpacity.toFixed(3);
      }
      if (Math.abs(wordmarkTranslateY - last.wordmarkY) > 0.05) {
        wordmarkEl.style.transform = `translate3d(0, ${wordmarkTranslateY.toFixed(
          2
        )}vh, 0)`;
        last.wordmarkY = wordmarkTranslateY;
      }
    }

    const scrollHintEl = scrollHintRef.current;
    if (scrollHintEl && Math.abs(rightOpacity - last.rightOpacity) > 0.005) {
      scrollHintEl.style.opacity = rightOpacity.toFixed(3);
    }
    last.rightOpacity = rightOpacity;

    const quoteWrapper = quoteWrapperRef.current;
    if (quoteWrapper && Math.abs(quoteOpacity - last.quoteOpacity) > 0.005) {
      quoteWrapper.style.opacity = quoteOpacity.toFixed(3);
      last.quoteOpacity = quoteOpacity;
    }

    // 3) Word colours — quantised alpha so we only touch spans on real changes
    if (quoteOpacity > 0) {
      const spans = wordSpansRef.current;
      const alphaCache = lastWordAlphaRef.current;
      for (let i = 0; i < REVEAL_WORDS.length; i++) {
        const wordProgress = Math.max(
          0,
          Math.min(1, revealProgress * REVEAL_WORDS.length - i)
        );
        const alpha = 0.18 + wordProgress * 0.82;
        const bucket = Math.round(alpha * 20);
        if (bucket !== alphaCache[i]) {
          alphaCache[i] = bucket;
          const span = spans[i];
          if (span) {
            span.style.color = `oklch(var(--foreground-950) / ${(
              bucket / 20
            ).toFixed(3)})`;
          }
        }
      }
    }
  };

  // Prime the per-word alpha cache before scroll callbacks fire.
  useEffect(() => {
    lastWordAlphaRef.current = new Array(REVEAL_WORDS.length).fill(-1);
  }, []);

  // Subscribe to the shared global scroll broadcaster.
  // No per-section scroll listener, no per-section rAF, no
  // `getBoundingClientRect()` on the scroll path — Hero simply reads its
  // 0..1 progress from a cached offsetTop/offsetHeight calculation.
  useSectionScrollProgress(sectionRef, (p, inView) => {
    inViewRef.current = inView;
    if (!inView) return;
    progressRef.current = p;
    // Shared broadcaster already runs at most once per animation frame,
    // so paint directly rather than adding another rAF hop.
    paint();
  });

  return (
    <section
      ref={sectionRef}
      id="home"
      className="relative w-full text-foreground-950"
      style={{
        height: "500vh",
        contain: "layout paint",
        isolation: "isolate",
      }}
    >
      <div
        className="sticky top-0 w-full h-screen overflow-hidden bg-background-50"
        style={{ contain: "layout paint" }}
      >
        {/* Poster video — shows the first frame instantly while bitmaps
            are being extracted in the background. Fades out the moment the
            first bitmap frame is ready, so there's never a blank state.
            Once hidden, its src is stripped so it stops decoding entirely. */}
        <video
          ref={posterVideoRef}
          className="absolute inset-0 w-full h-full object-cover"
          muted
          playsInline
          preload="auto"
          aria-hidden="true"
          style={{
            opacity: hasFirstFrame ? 0 : 1,
            transition: "opacity 0.4s ease-out",
            pointerEvents: "none",
            display: hasFirstFrame ? "none" : "block",
          }}
        />

        {/* Canvas — sized to viewport, receives cropped bitmap draws (no CSS transform).
            Shown as soon as it mounts; the poster underneath covers any
            gap before the first bitmap paints, so there's no visible loading. */}
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full"
          style={{
            width: "100%",
            height: "100%",
            backfaceVisibility: "hidden",
          }}
        />

        {/* Decorative circular guides — promoted to their own compositor layer
            so they don't re-composite with the canvas on every scroll frame. */}
        <div
          className="pointer-events-none absolute inset-0 flex items-center justify-center will-change-transform"
          style={{ transform: "translateZ(0)" }}
        >
          <div className="absolute w-[120vh] h-[120vh] rounded-full border border-foreground-950/15"></div>
          <div className="absolute w-[95vh] h-[95vh] rounded-full border border-foreground-950/12"></div>
          <div className="absolute w-[70vh] h-[70vh] rounded-full border border-foreground-950/10"></div>
        </div>

        {/* Scroll-linked highlight reveal quote */}
        <div
          ref={quoteWrapperRef}
          className="absolute inset-0 z-20 flex items-center justify-center px-6 pointer-events-none"
          style={{ opacity: 0 }}
        >
          <p
            className="font-heading italic text-center leading-[1.35] max-w-[1040px] mx-auto"
            style={{
              fontSize: "clamp(2.15rem, 3.6vw, 3.6rem)",
              letterSpacing: "-0.005em",
              fontWeight: 400,
              fontVariationSettings: "'opsz' 144",
            }}
          >
            {REVEAL_WORDS.map((word, i) => (
              <span
                key={`w-${i}`}
                ref={(el) => {
                  wordSpansRef.current[i] = el;
                }}
                style={{ color: "oklch(var(--foreground-950) / 0.18)" }}
              >
                {word}
                {i < REVEAL_WORDS.length - 1 ? " " : ""}
              </span>
            ))}
          </p>
        </div>

        {/* Content */}
        <div className="relative z-30 flex flex-col justify-between h-screen pt-28 md:pt-32 pb-0 px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-10 max-w-[1600px] mx-auto w-full">
            <div
              ref={leftBlockRef}
              className="max-w-sm will-change-transform"
              style={{
                opacity: 1,
                transform: "translate3d(0, 0vh, 0)",
              }}
            >
              <p className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/70 mb-5 font-medium">
                Psychological Counseling
              </p>
              <p className="font-sans text-[15px] md:text-base leading-[1.55] text-foreground-950">
                Compassionate therapy and a calm, private space help you slow
                down, reconnect with yourself, and turn self-care into a quiet
                daily practice.
              </p>
            </div>

            <div className="flex md:justify-end md:mt-8">
              <Link
                to="/contacts"
                className="group inline-flex items-center gap-3 bg-foreground-950 text-background-50 rounded-full pl-8 pr-2 py-2 whitespace-nowrap cursor-pointer transition-transform duration-300 hover:-translate-y-0.5"
              >
                <span className="text-[11px] tracking-[0.3em] uppercase font-medium">
                  Book a Session
                </span>
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 transition-transform duration-300 group-hover:translate-x-0.5">
                  <i className="ri-arrow-right-up-line text-lg"></i>
                </span>
              </Link>
            </div>
          </div>
        </div>

        {/* Giant headline wordmark */}
        <div
          ref={wordmarkRef}
          className="absolute z-10 flex pointer-events-none will-change-transform pr-6 md:pr-10"
          style={{
            right: 0,
            bottom: "60px",
            opacity: 1,
            transform: "translate3d(0, 0vh, 0)",
          }}
        >
          <h1
            className="font-heading text-foreground-950 select-none whitespace-nowrap text-right"
            style={{
              fontSize: "clamp(5rem, 18vw, 18rem)",
              letterSpacing: "-0.055em",
              lineHeight: "1",
              paddingBottom: "0.05em",
              fontWeight: 400,
              fontVariationSettings: "'opsz' 144",
            }}
          >
            serene
          </h1>
        </div>

        {/* Scroll hint */}
        <div
          ref={scrollHintRef}
          className="absolute bottom-8 right-6 md:right-10 z-30 hidden md:flex items-center gap-3 text-foreground-950/70"
          style={{ opacity: 1 }}
        >
          <span className="text-[10px] tracking-[0.3em] uppercase font-medium">Scroll</span>
          <span className="w-8 h-[1px] bg-foreground-950/50"></span>
        </div>
      </div>
    </section>
  );
}